/**
 * Input validation utilities for preventing invalid characters in input fields
 */

import { toaster } from '../ui/Toaster/Toaster';

const FULL_WIDTH_AMPERSAND = '＆'; // Unicode U+FF06

export const DEFAULT_FORBIDDEN_CHARS = [FULL_WIDTH_AMPERSAND];

/**
 * Checks if a string contains any forbidden characters
 */
export function containsForbiddenChars(value: string, forbiddenChars: string[] = DEFAULT_FORBIDDEN_CHARS): boolean {
    return forbiddenChars.some((char) => value.includes(char));
}

/**
 * Creates a regex pattern for forbidden characters
 */
function createForbiddenCharsRegex(forbiddenChars: string[]): RegExp {
    const escapedChars = forbiddenChars.map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return new RegExp(`[${escapedChars.join('')}]`, 'g');
}

/**
 * Returns an object with the cleaned value and information about what was removed
 */
export function cleanForbiddenChars(
    value: string,
    forbiddenChars: string[] = DEFAULT_FORBIDDEN_CHARS
): { cleanedValue: string; wasModified: boolean; removedChars: string[] } {
    const originalValue = value;
    const regex = createForbiddenCharsRegex(forbiddenChars);
    const cleanedValue = value.replace(regex, '');
    const wasModified = cleanedValue !== originalValue;
    const removedChars = wasModified ? forbiddenChars.filter((char) => originalValue.includes(char)) : [];

    return { cleanedValue, wasModified, removedChars };
}

/**
 * Shows a toast notification for removed forbidden characters
 */
export function showForbiddenCharsToast(
    removedChars: string[],
    fieldName?: string,
    anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
): void {
    if (removedChars.length === 0) {
        return;
    }

    let message = `Invalid character(s): ${removedChars.join(', ')}`;

    if (fieldName) {
        message = `Invalid character(s) in ${fieldName}: ${removedChars.join(', ')}`;
    }

    toaster.makeToast({
        severity: 'error',
        message,
        autoHideDuration: 5000,
        anchorOrigin: anchorOrigin || { vertical: 'top', horizontal: 'left' },
    });
}

/**
 * Removes forbidden characters from a string and optionally shows a toast notification
 * This is the main convenience function that combines cleaning and notification
 */
export function removeForbiddenChars(
    value: string,
    forbiddenChars: string[] = DEFAULT_FORBIDDEN_CHARS,
    showToast: boolean = false,
    fieldName?: string,
    anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
): string {
    const { cleanedValue, wasModified, removedChars } = cleanForbiddenChars(value, forbiddenChars);

    if (showToast && wasModified) {
        showForbiddenCharsToast(removedChars, fieldName, anchorOrigin);
    }

    return cleanedValue;
}
